/**
 * Which Word-pane actions count as an insert, and whether they landed.
 *
 * Event names stay the ones PostHog already charts (`word_addin_insert_succeeded`)
 * plus the failure pair. Properties are a kind and a count: never document
 * text, and never the error string Word returned.
 */

const INSERT_KINDS = new Set([
  "pull",
  "pull_from_mytender",
  "edit_document",
  "insert_document_image",
  "write_question_answer",
  "insert_template_pages",
]);

const SUCCESS = new Set(["added", "replaced", "applied", "inserted"]);

/** A pull that did not change the document is not a failed insert. */
const PULL_FAILURE = new Set(["error"]);

/** An edit or image that did not land, including a refused table edit. */
const WRITE_FAILURE = new Set([
  "error",
  "stale",
  "missing",
  "unsupported",
  "failed",
  "in_table",
  "has_revisions",
  "merged_cells",
]);

const PULL_KINDS = new Set(["pull", "pull_from_mytender", "write_question_answer"]);

export type InsertTally = { succeeded: number; failed: number };

export const isDocumentInsert = (kind: string): boolean => INSERT_KINDS.has(kind);

const rowsOf = (result: unknown): { status?: string }[] => {
  if (!result || typeof result !== "object") return [];
  const record = result as Record<string, unknown>;
  if (Array.isArray(record.outcomes)) {
    return record.outcomes.filter((row): row is { status?: string } => !!row && typeof row === "object");
  }
  if (record.outcome && typeof record.outcome === "object") {
    return [record.outcome as { status?: string }];
  }
  if (typeof record.status === "string") return [{ status: record.status }];
  return [];
};

/**
 * How many writes landed, and how many did not.
 * @param kind - The pane action or Word tool name.
 * @param result - The tool or pull result. Shape varies by tool.
 * @returns Counts, or null when this action is not an insert or nothing was attempted.
 */
export const insertTally = (kind: string, result: unknown): InsertTally | null => {
  if (!INSERT_KINDS.has(kind)) return null;
  const fail = PULL_KINDS.has(kind) ? PULL_FAILURE : WRITE_FAILURE;
  let succeeded = 0;
  let failed = 0;
  for (const row of rowsOf(result)) {
    const status = row.status;
    if (!status) continue;
    if (SUCCESS.has(status)) succeeded += 1;
    else if (fail.has(status)) failed += 1;
  }
  if (succeeded + failed === 0) return null;
  return { succeeded, failed };
};
